import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { toast } from "react-toastify";
import isGHMC, { formatDate } from "../../../Common/helper/helpar";

const EXPORT_HEADERS = [
    "S.No",
    "Employee ID",
    "Employee Name",
    "Department",
    "Designation",
    "Zone",
    "Ward",
    "Identity NO",
    "Gender",
    "Status",
    "Contact No",
    "Shift",
    "Joining Date",
    "Date of Leaving",
    "KPI Category",
    "Last Date Modified",
    "Last Modified By",
];

const safeFormatDate = (value) => {
    if (!value) return "N/A";
    try {
        return formatDate(value) || "N/A";
    } catch (error) {
        return "N/A";
    }
};

const getStatusLabel = (item) => {
    if (item?.status === "Active" || item?.status === "InActive" || item?.status === "Inactive") {
        return item.status === "Inactive" ? "InActive" : item.status;
    }
    return item?.isActive ? "Active" : "InActive";
};

const mapEmployeeRow = (item, index) => [
    index + 1,
    item.empCode || item.employeeId || "N/A",
    item.employeeName || "N/A",
    item.departmentName || item.operationName || "N/A",
    item.designationName || "N/A",
    item.zoneName || "N/A",
    item.wardNo || item.wardName || item.circleName || "N/A",
    item.identityNo || "N/A",
    item.gender || "N/A",
    getStatusLabel(item),
    item.contactNo || "N/A",
    item.shiftName || "N/A",
    safeFormatDate(item.dateOfJoining),
    safeFormatDate(item.dateOfExit),
    item.kpiCategoryCode || "N/A",
    safeFormatDate(item.lastModifiedOn),
    item.lastModifiedBy || "N/A",
];

const downloadBlobFile = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    anchor.style.display = "none";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    // Delay revoke so browser can finish reading the blob (avoids empty/corrupt downloads)
    setTimeout(() => URL.revokeObjectURL(url), 1500);
};

export const ExportEmployee = ({ employeeData = [] }) => {
    const isProd = isGHMC();
    const headerArgb = isProd ? "FF043555" : "FFEC1117";
    const headerRgb = isProd ? [4, 53, 85] : [236, 17, 23];
    const rows = Array.isArray(employeeData) ? employeeData : [];

    const exportToExcel = async () => {
        if (rows.length === 0) {
            toast.warning("No employee data available to export.");
            return;
        }

        try {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet("Employee");

            const boldFont = { bold: true };
            const currentDate = new Date();
            const formattedDate = `${currentDate.getDate()}-${currentDate.getMonth() + 1}-${currentDate.getFullYear()}`;
            const formattedTime = `${currentDate.getHours()}:${currentDate.getMinutes()}:${currentDate.getSeconds()}`;
            const downloadDateTime = `Employee Details Generated on: ${formattedDate}, ${formattedTime}`;

            worksheet.addRow([downloadDateTime]);
            worksheet.mergeCells(1, 1, 1, EXPORT_HEADERS.length);
            worksheet.getCell(1, 1).font = boldFont;
            worksheet.getCell(1, 1).alignment = { horizontal: "center" };

            worksheet.addRow(["Employee Details"]);
            worksheet.mergeCells(2, 1, 2, EXPORT_HEADERS.length);
            worksheet.getCell(2, 1).font = boldFont;
            worksheet.getCell(2, 1).alignment = { horizontal: "center" };

            const tableHeaderRow = worksheet.addRow(EXPORT_HEADERS);
            tableHeaderRow.eachCell((cell) => {
                cell.font = { ...boldFont, color: { argb: "FFFFFFFF" } };
                cell.alignment = { horizontal: "center", vertical: "middle" };
                cell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: headerArgb },
                };
            });

            rows.forEach((item, index) => {
                const row = worksheet.addRow(mapEmployeeRow(item, index));
                row.eachCell((cell) => {
                    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
                });
            });

            worksheet.columns = [
                { width: 8 },
                { width: 16 },
                { width: 22 },
                { width: 18 },
                { width: 18 },
                { width: 16 },
                { width: 12 },
                { width: 16 },
                { width: 12 },
                { width: 12 },
                { width: 14 },
                { width: 14 },
                { width: 14 },
                { width: 14 },
                { width: 14 },
                { width: 16 },
                { width: 18 },
            ];

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            downloadBlobFile(blob, "Employee.xlsx");
        } catch (error) {
            console.error("Error generating Excel file:", error);
            toast.error("Unable to generate Excel file. Please try again.");
        }
    };

    const exportToPDF = () => {
        if (rows.length === 0) {
            toast.warning("No employee data available to export.");
            return;
        }

        try {
            const doc = new jsPDF("landscape", "mm", "a4");
            const currentDate = new Date();
            const formattedDate = `${currentDate.getFullYear()}-${currentDate.getMonth() + 1}-${currentDate.getDate()}`;
            const formattedTime = `${currentDate.getHours()}:${currentDate.getMinutes()}:${currentDate.getSeconds()}`;
            const downloadDateTime = `Employee Details Generated on: ${formattedDate} ${formattedTime}`;

            doc.setFont("helvetica", "bold");
            doc.setFontSize(14);
            doc.text("Employee Details", 14, 16);
            doc.setFontSize(10);
            doc.text(downloadDateTime, 14, 24);

            autoTable(doc, {
                startY: 28,
                head: [EXPORT_HEADERS],
                body: rows.map((item, index) => mapEmployeeRow(item, index)),
                headStyles: {
                    fillColor: headerRgb,
                    textColor: [255, 255, 255],
                    fontStyle: "bold",
                    fontSize: 7,
                    halign: "center",
                },
                styles: {
                    fontSize: 6,
                    halign: "center",
                    cellPadding: 1.5,
                    overflow: "linebreak",
                },
                margin: { left: 8, right: 8, top: 20 },
            });

            doc.save("EmployeeDetails.pdf");
        } catch (error) {
            console.error("Error generating PDF file:", error);
            toast.error("Unable to generate PDF file. Please try again.");
        }
    };

    return (
        <>
            <button className="button-container" onClick={exportToExcel}>
                <i className="fa-solid fa-file-excel me-1 icon-container"></i>
                Excel
            </button>
            <button className="button-container" onClick={exportToPDF}>
                <i className="fa-solid fa-file-pdf me-1 icon-container"></i>
                PDF
            </button>
        </>
    );
};
